/**
 * Landing-feature i18n namespace registration (design-revamp).
 *
 * The `landing` namespace is registered here via `i18n.addResourceBundle`
 * rather than in `apps/web/src/lib/i18n.ts` (which is out of this screen's
 * allowed-touch set). The bundles load once at module import; the landing
 * screen also calls `registerLandingI18n()` at the top of render so the keys
 * are present regardless of import order (the same idiom the `account` and
 * `chat` features use).
 *
 * All three locales ship (ADR-028: RU/KY/EN first-class at MVP). A missing
 * KY/EN key falls back to RU (REQ-PRD-§13.2-A) — RU is the fallback locale.
 *
 * `addResourceBundle` only exists once the singleton is initialized
 * (`initI18n()` runs in `main.tsx` at boot and in each test's `beforeEach`),
 * so we guard on `i18n.isInitialized` + the method's presence and remember
 * that we have run (HMR-/double-call-safe).
 */
import i18n from "@/lib/i18n";

import landingEn from "@/locales/en/landing.json";
import landingKy from "@/locales/ky/landing.json";
import landingRu from "@/locales/ru/landing.json";

/** The i18next namespace owned by the landing feature. */
export const LANDING_NS = "landing";

let landingI18nRegistered = false;

/**
 * Register the `landing` i18n namespace (ru/ky/en) on the shared i18next
 * singleton. Idempotent and init-safe.
 *
 * The 4th `addResourceBundle` arg `deep=true` merges nested objects; the 5th
 * `overwrite=false` keeps an existing bundle so HMR re-imports don't clobber
 * a live bundle.
 */
export function registerLandingI18n(): void {
  if (landingI18nRegistered) return;
  if (!i18n.isInitialized || typeof i18n.addResourceBundle !== "function") {
    return;
  }
  i18n.addResourceBundle("ru", LANDING_NS, landingRu, true, false);
  i18n.addResourceBundle("ky", LANDING_NS, landingKy, true, false);
  i18n.addResourceBundle("en", LANDING_NS, landingEn, true, false);
  landingI18nRegistered = true;
}

// Best-effort eager registration for the production path (initI18n() runs
// before any feature module imports in main.tsx). In tests where init happens
// in beforeEach, the screen calls registerLandingI18n() on render.
registerLandingI18n();
